// MACHINERY V2 (Stroje) — V4 shop-floor wall: tile surface lookup map (M2.14).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before any of this was written).
//
// >>> WHY THIS IS A LOOKUP MAP AND NOT A STYLESHEET. <<< The manager's
// token/atom reconciliation ruling for the views wave: V4's status band
// surfaces "land as inline lookup maps (`wallTileStyles`) beside
// cardStateStyles.ts, NOT CSS classes (hover-free surfaces need no
// stylesheet)". The wall's TILES have no hover, focus or active state to
// express, so the only thing a class buys them is a second place for a colour
// to live. Same closed-enum-map idiom as STATUS_SPECS (cardStates.ts) and the
// builders in cardStateStyles.ts.
//
// (The header's exit X, added by owner ruling 2026-08-24, DOES have hover and
// focus — which is exactly why that one control reuses the shipped
// `.btn btn-ghost` classes instead of a map like this one. A `:hover` rule
// cannot live in an inline style; see the token sheet's own note at
// machinery-v2-tokens.scss:162-164. It changes nothing here.)
//
// >>> KEYED ON THE CONTRACT, NOT ON RAW TELEMETRY. <<< WALL_TILE_SPECS is a
// `Record<MachineryV2CardState, …>`, so the compiler forces an entry for all
// nine canonical states and a tile can never fall through to an untinted
// surface. The foreground ink comes from the SHIPPED statusColors() in
// cardStateStyles.ts keyed on `presentation.badgeStatus`, so the big status
// numerals can never disagree with the badge printed above them.
//
// PALETTE DELTA IS ZERO (reconciliation ruling): every var below is already in
// machinery-v2-tokens.scss, and both palettes resolve them under
// `.machinery-v2-root`. Nothing here adds a colour.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import type { CSSProperties } from "react";

import {
  MachineryV2CardPresentation,
  MachineryV2CardState,
  MachineryV2StatusToken,
} from "../cardStates";
import { statusColors } from "../cardStateStyles";

export interface WallTileSpec {
  token: MachineryV2StatusToken;
  /** Fill = `color-mix(in oklab, var(token) fill%, var(--card))`. */
  fill: number;
  /** Border = `color-mix(in oklab, var(token) border%, transparent)`. */
  border: number;
}

/**
 * The README's "tile fill saturates with status" table, 16–28%, verbatim from
 * the prototype's `tokenMap` — plus the three states the 5-state prototype does
 * not have and our 9-state contract does:
 *
 *   awaiting     — a connection row exists but nothing has ever been reported.
 *                  Neutral 10%: louder than `unconnected` (something IS there)
 *                  and quieter than any status (nothing has been said yet).
 *   stale        — connected, feed dead, status unknown. Neutral 10% for the
 *                  same reason: tinting it green because the machine was
 *                  running two hours ago is exactly the lie the wall must not
 *                  tell across a hall.
 *   stale-alarm  — an alarm is an alarm. Same red 28% as `alarm`; the tile then
 *                  carries `opacity: 0.78` from the stale look, which is the
 *                  "last known" signal, and the badge desaturates with it.
 *
 * Borders: 60% mix for every connected state, 30% for the two that are not
 * (README: "border 60% mix; unconnected = 6% neutral").
 */
export const WALL_TILE_SPECS: Record<MachineryV2CardState, WallTileSpec> = {
  unconnected: { token: "--faint", fill: 6, border: 30 },
  awaiting: { token: "--faint", fill: 10, border: 30 },
  running: { token: "--green", fill: 22, border: 60 },
  idle: { token: "--amber", fill: 22, border: 60 },
  stopped: { token: "--muted", fill: 18, border: 60 },
  alarm: { token: "--red", fill: 28, border: 60 },
  "stale-alarm": { token: "--red", fill: 28, border: 60 },
  stale: { token: "--faint", fill: 10, border: 60 },
  offline: { token: "--faint", fill: 16, border: 60 },
};

const mix = (token: MachineryV2StatusToken, pct: number, base: string): string =>
  "color-mix(in oklab, var(" + token + ") " + pct + "%, " + base + ")";

/**
 * The tile shell. `minHeight: 280`, `borderRadius: 14`, `gap: 10` and the
 * `18px 20px 16px` padding are the artboard's, unchanged.
 *
 * NO transition, NO hover, NO cursor: it is a TV. `position: relative` exists
 * only so the connectivity dot can pin to the corner — not for an overlay.
 */
export const wallTileStyle = (p: MachineryV2CardPresentation): CSSProperties => {
  const spec = WALL_TILE_SPECS[p.state];
  return {
    position: "relative",
    overflow: "hidden",
    background: mix(spec.token, spec.fill, "var(--card)"),
    border: "1px solid " + mix(spec.token, spec.border, "transparent"),
    borderRadius: 14,
    padding: "18px 20px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minHeight: 280,
    // The one place the stale look is applied on this surface — taken from the
    // contract, never re-tested from `conn`. `offline` is deliberately NOT
    // stale-looking (see MachineryV2CardPresentation.staleLook).
    opacity: p.staleLook ? 0.78 : 1,
  };
};

/**
 * The ink for the tile's BIG status-coloured numerals (elapsed, SR count).
 *
 * The prototype mixes the status token 78% toward --ink; the shipped
 * statusColors() mixes 82%. We take the SHIPPED one — the 4-point difference is
 * invisible at wall distance, and routing this through the same function every
 * badge already uses is what makes "one status contract" true for colour and
 * not just for state. Falls back to --ink-2 when there is no status to paint
 * (unconnected / awaiting / status-less stale), which is also when those
 * numerals have nothing to say.
 */
export const wallStatusInk = (p: MachineryV2CardPresentation): string =>
  p.badgeStatus ? statusColors(p.badgeStatus, p.staleLook).fg : "var(--ink-2)";

/**
 * ============================================================
 *  THE SAME SURFACE, AT ROW SCALE — V4 BELOW 1280 (M2.16b)
 * ============================================================
 * Rendered truth: mobile/screenshots/12-v4-phone.png and
 * 13-v4-tablet-1024.png, both viewed before this was written. Reference impl:
 * mobile/machinery-mobile.jsx MbV4Row (:383-411).
 *
 * The board's own rule is `conn ? (alarm ? 16 : 10) : 4` percent fill and
 * `conn ? 40 : 20` percent border — three buckets. IT IS EXPRESSED HERE AS A
 * NINE-STATE RECORD ANYWAY, for the same reason WALL_TILE_SPECS is: the
 * compiler then forces an entry for every canonical state and a row can never
 * fall through to an untinted surface. Mapping the board's three buckets onto
 * the contract: the two alarm states take 16, every other CONNECTED state takes
 * 10, and `unconnected` — the only state that is not a connection at all — takes
 * 4.
 *
 * WHY NOT REUSE WALL_TILE_SPECS. Those are 6-28% fills tuned for a 380px tile
 * seen from across a hall. The same saturation on a 62px row held at arm's
 * length is a wall of colour blocks with the machine name fighting the fill, and
 * the board says so: it halves them. Two scales of the SAME idiom, in one file,
 * rather than one map that is wrong at one of them.
 *
 * PALETTE DELTA IS STILL ZERO — every token below already exists in
 * machinery-v2-tokens.scss, and `color-mix` on a status token is the module's
 * established idiom. No hex is introduced.
 */
export const WALL_ROW_SPECS: Record<MachineryV2CardState, WallTileSpec> = {
  unconnected: { token: "--faint", fill: 4, border: 20 },
  awaiting: { token: "--faint", fill: 10, border: 40 },
  running: { token: "--green", fill: 10, border: 40 },
  idle: { token: "--amber", fill: 10, border: 40 },
  stopped: { token: "--muted", fill: 10, border: 40 },
  alarm: { token: "--red", fill: 16, border: 40 },
  "stale-alarm": { token: "--red", fill: 16, border: 40 },
  stale: { token: "--faint", fill: 10, border: 40 },
  offline: { token: "--faint", fill: 10, border: 40 },
};

/**
 * The compact row shell. `minHeight: 62`, `borderRadius: 11`, `gap: 11` and the
 * `11px 13px` padding are the board's, unchanged.
 *
 * `opacity: 0.8` on the stale look is the board's too (MbV4Row's `m.stale ? 0.8`)
 * — a shade lighter than the 0.78 the big tile uses, and taken from the drawing
 * rather than reconciled to it. It comes from the CONTRACT (`staleLook`), never
 * from a re-test of `conn`, so `offline` is deliberately NOT dimmed: its own
 * cloud-off badge is already the staleness verdict.
 *
 * NO hover, NO cursor, NO transition: like the tile, this is a monitoring
 * surface with nothing to activate.
 */
export const wallRowStyle = (p: MachineryV2CardPresentation): CSSProperties => {
  const spec = WALL_ROW_SPECS[p.state];
  return {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "11px 13px",
    borderRadius: 11,
    minHeight: 62,
    minWidth: 0,
    background: mix(spec.token, spec.fill, "var(--card)"),
    border: "1px solid " + mix(spec.token, spec.border, "transparent"),
    opacity: p.staleLook ? 0.8 : 1,
  };
};

/**
 * The 5px bar down the row's left edge.
 *
 * The board paints `conn ? statusFg(status) : var(--border)`; on the contract
 * that is `badgeStatus`, so `unconnected`, `awaiting` and a status-less `stale`
 * all fall to the neutral border colour instead of asserting a status we do not
 * have. It is the SAME rule FleetRow applies to its own 4px status edge
 * (FleetRow.tsx:73, re-read while writing this) — the only difference is that a
 * row on a monitoring wall always draws the bar, in neutral, rather than
 * dropping it, so the rows stay optically aligned down the left.
 */
export const wallRowBar = (p: MachineryV2CardPresentation): string =>
  p.badgeStatus ? statusColors(p.badgeStatus, p.staleLook).fg : "var(--border)";
